import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import type { ChangeEvent, ComponentRef, MouseEvent, Ref } from 'react';
import React, { useEffect, useId, useState } from 'react';
import type { InputProps } from 'types';

import {
  inputLTElementsVariants,
  inputVariants,
} from './variants/input-variants';
import {
  contentWrapper,
  inputRootVariants,
} from './variants/input-wrapper-variants';

export const Input = ({
  inputSize,
  id,
  value,
  onChange,
  chipsContainer,
  leadingElement,
  trailingElement,
  search = false,
  clearable = true,
  classNames = {},
  onClear,
  error = false,
  rawProps = {},
  ref,
  ...restProps
}: InputProps & {
  ref?: Ref<HTMLInputElement>;
}) => {
  const inputGenId = useId();
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange?.(e);
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    setInputValue('');
    onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
    onClear?.(event);
  };

  const inputId = id || inputGenId;

  const focusInput = () => {
    document.getElementById(inputId)?.focus();
  };

  const isDisabled = restProps.disabled;

  return (
    <div
      className={cn([
        inputRootVariants({ inputSize, hasError: error }),
        classNames.wrapper,
      ])}
      onClick={focusInput}
    >
      <div
        id="wrapper"
        className="relative flex w-full items-center justify-start"
      >
        {leadingElement && (
          <div
            className={cn(
              inputLTElementsVariants({
                isString: typeof leadingElement === 'string',
                isDisabled,
              }),
              classNames.leadingElement,
            )}
          >
            {leadingElement}
          </div>
        )}
        <div
          id="contentWrapper"
          className={cn([
            contentWrapper({ inputSize }),
            classNames.contentWrapper,
          ])}
        >
          <div className="flex flex-1 flex-wrap gap-1">
            {chipsContainer}
            <input
              ref={ref}
              id={inputId}
              value={inputValue}
              onChange={handleInputChange}
              aria-invalid={error}
              className={cn(
                inputVariants({
                  isDisabled,
                  inputSize,
                }),
                classNames.input,
              )}
              {...restProps}
              {...rawProps}
              readOnly={!!chipsContainer || restProps.readOnly}
              onWheel={(event) => event.currentTarget.blur()} // disables number input scroll
            />
          </div>
        </div>
        {clearable &&
          inputValue &&
          !restProps.disabled &&
          !restProps.readOnly && (
            <IconButton
              size="tiny"
              type="ghost"
              variant="neutral"
              onClick={handleClear}
              data-testid="clear-button"
              htmlType="button"
              aria-label="Clear"
              rawProps={{ tabIndex: -1 }}
              className={cn(
                inputLTElementsVariants({
                  isString: false,
                  isDisabled,
                }),
                classNames.clearButton,
              )}
              icon={
                <Icon
                  name="x-circle"
                  className="fill-current"
                  data-testid="input-clear"
                />
              }
            />
          )}
        {search && (
          <IconButton
            size="tiny"
            type="ghost"
            variant="strong"
            htmlType="button"
            aria-label="Search"
            data-testid="search-button"
            disabled={isDisabled}
            // Decorative affordance: the input itself owns focus and search
            // semantics, so keep the button out of the tab order and the
            // a11y tree (mirrors the presentational IconButton pattern).
            rawProps={{ tabIndex: -1, 'aria-hidden': true }}
            className={classNames.trailingElement}
            icon={
              <Icon
                name="search"
                className="fill-current"
                data-testid="input-search"
              />
            }
          />
        )}
        {trailingElement && (
          <div
            className={cn(
              inputLTElementsVariants({
                isString: typeof trailingElement === 'string',
                isDisabled,
              }),
              classNames.trailingElement,
            )}
          >
            {trailingElement}
          </div>
        )}
      </div>
    </div>
  );
};

Input.displayName = 'Input';

type DebouncedInputProps = Omit<InputProps, 'onChange'> & {
  onChange?: (value: InputProps['value']) => void;
  debounce?: number;
};

export const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ref,
  ...props
}: DebouncedInputProps & {
  ref?: Ref<ComponentRef<typeof Input>>;
}) => {
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange?.(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);
  return (
    <Input
      ref={ref}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setValue(event.target.value)
      }
      {...props}
    />
  );
};

DebouncedInput.displayName = 'DebouncedInput';

export { type InputProps };
